import {
  Box,
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Heading,
  Icon,
  Input,
  InputGroup,
  InputLeftAddon,
  Spinner,
  Text,
  useToast,
} from "@chakra-ui/react";
import { isFinite, isNil, parseInt } from "lodash";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { fractionToEditablePercent } from "../../../../../../common/formatting/interestRate";
import { FaChartLine, FaDownload } from "react-icons/fa";
import { ContractDto, IfrsValuesDto } from "../../../../../../autogen/bff-api";
import { formatDecimalForInput, parseDecimalInput } from "../../../../../../common/input/parseDecimalInput";
import { usePendingSaves } from "../../../../../../common/input/pendingSaves";
import { DateFormControl } from "../../../../../../common/input/DateTimeSelectors/DateFormControl";
import { DateSelector } from "../../../../../../common/input/DateTimeSelectors/DateSelector";
import {
  UnsavedChangesError,
  useCreateEditor,
  useEditor,
  useFieldErrorTranslation,
} from "../../../../../../common/editing/save-coordinator";
import { useApiError } from "../../../../../../common/errors/useApiError";
import { Explanation } from "../../../../../../common/support/Explanation/Explanation";
import { downloadContractIfrsReport } from "../../../../ifrsReportDownload";
import { useContractState } from "../../../useContractState";
import { RemoveDataFieldButton } from "../RemoveDataFieldButton";
import { IfrsAdjustmentsModal } from "./ifrs-adjustments/IfrsAdjustmentsModal";
import { parseInterestRateInput } from "./parseInterestRateInput";
import { hasValuesForIfrsReport } from "./ifrsReportReadiness";
import { editableIfrsValues, EditableIfrsValues, useSaveIfrsValues } from "./useSaveIfrsValues";

interface Props {
  removeDataField?: () => Promise<void>;
  isRequiredButNotProvided: boolean;
}

export const IfrsValues = ({ removeDataField, isRequiredButNotProvided }: Props) => {
  const contractState = useContractState();
  const savedIfrsValues = contractState.contract.dataFields?.ifrsValues;
  if (!savedIfrsValues) return <IfrsValuesLoading />;
  return (
    <IfrsValuesForm
      removeDataField={removeDataField}
      isRequiredButNotProvided={isRequiredButNotProvided}
      savedIfrsValues={savedIfrsValues}
    />
  );
};

const IfrsValuesLoading = () => {
  const { t } = useTranslation();
  return <p>{t("Loading")}...</p>;
};

const IfrsValuesForm = ({
  removeDataField,
  isRequiredButNotProvided,
  savedIfrsValues,
}: Props & { savedIfrsValues: IfrsValuesDto }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const { trigger } = useApiError();
  const formatFieldError = useFieldErrorTranslation();

  const contractState = useContractState();
  const contract = contractState.contract;
  const savedValuesFilled = savedIfrsValues.filled === true;
  const adjustments = savedIfrsValues.adjustments;

  const saveIfrsValues = useSaveIfrsValues(contract.id);
  const editorId = `${contract.id}:ifrs-values`;
  const initialState = useMemo(() => editableIfrsValues(savedIfrsValues), [savedIfrsValues]);
  const lastSaved = useRef(initialState);
  useEffect(() => {
    lastSaved.current = initialState;
  }, [initialState]);

  const save = useCallback(
    async (fields: Partial<EditableIfrsValues>): Promise<EditableIfrsValues> => {
      const saved = await saveIfrsValues({ ...lastSaved.current, ...fields });
      lastSaved.current = editableIfrsValues(saved.dataFields?.ifrsValues);
      return lastSaved.current;
    },
    [saveIfrsValues]
  );
  const editorConfig = useMemo(() => ({ initialState, save, onError: trigger }), [initialState, save, trigger]);
  useCreateEditor<EditableIfrsValues>(editorId, editorConfig);
  const { state: ifrsValues, updateField, flushPending, entityStatus, fieldError } =
    useEditor<EditableIfrsValues>(editorId);
  const pendingSaves = usePendingSaves();
  useEffect(() => {
    const pending = { flush: () => flushPending().catch(() => undefined) };
    pendingSaves?.add(pending);
    return () => {
      pendingSaves?.delete(pending);
    };
  }, [flushPending, pendingSaves]);

  const currency = contract.currency;
  const monthlyPaymentBeforeEdit = useRef<number | null | undefined>(undefined);
  const [monthlyPaymentText, setMonthlyPaymentText] = useState<string>(() => {
    const payment = savedIfrsValues.monthlyPayment;
    return isNil(payment) ? "" : formatDecimalForInput(payment);
  });
  const [interestRate, setInterestRate] = useState<string>(() => {
    const ir = savedIfrsValues.interestRate;
    return !isNil(ir) && isFinite(ir) ? `${fractionToEditablePercent(ir)}` : "";
  });
  const [hasDeviatingFirstPeriod, setHasDeviatingFirstPeriod] = useState(!isNil(savedIfrsValues.startDateSecondPeriod));
  const [rejectedMonthInput, setRejectedMonthInput] = useState<Partial<Record<MonthField, string>>>({});
  const [showAddAdjustment, setShowAddAdjustment] = useState(false);
  const [isDownloadingReport, setIsDownloadingReport] = useState(false);

  const downloadReport = async () => {
    setIsDownloadingReport(true);
    try {
      await flushPending();
    } catch (e) {
      if (!(e instanceof UnsavedChangesError)) throw e;
      toast({
        description: t("The IFRS values are not saved, so the report was not downloaded. Try again once they are saved."),
        status: "error",
      });
      setIsDownloadingReport(false);
      return;
    }
    try {
      await downloadContractIfrsReport(contract);
    } catch {
      toast({ description: t("The report could not be downloaded. Try again."), status: "error" });
    } finally {
      setIsDownloadingReport(false);
    }
  };

  const handleIfrsValueChange = (change: Partial<EditableIfrsValues>) => updateField(change);

  const saveError = (field: keyof EditableIfrsValues) => {
    const error = fieldError(field);
    if (!error) return null;
    return (
      <Text role="alert" fontSize="sm" color="smError" mt="1">
        {formatFieldError(error)}
      </Text>
    );
  };

  const handleMonthsChange = (field: MonthField, input: HTMLInputElement) => {
    const raw = input.value;
    const trimmed = raw.trim();
    const months = trimmed ? parseInt(trimmed) : undefined;
    if (input.validity.badInput || isBelowOneMonth(months)) {
      setRejectedMonthInput((rejected) => ({ ...rejected, [field]: raw }));
      return;
    }
    setRejectedMonthInput(({ [field]: _cleared, ...rest }) => rest);
    handleIfrsValueChange({ [field]: months ?? null });
  };

  const monthInputValue = (field: MonthField) => rejectedMonthInput[field] ?? ifrsValues[field] ?? "";

  const isMonthFieldInvalid = (field: MonthField) =>
    rejectedMonthInput[field] !== undefined || isBelowOneMonth(ifrsValues[field]);

  const belowOneMonthMessage = (field: MonthField) =>
    rejectedMonthInput[field] !== undefined
      ? t("Must be at least 1 month. The value is not saved.")
      : t("Must be at least 1 month");

  const parsedMonthlyPayment = parseDecimalInput(monthlyPaymentText, { allowSpaceGrouping: true });
  const isMonthlyPaymentNotANumber = parsedMonthlyPayment.kind === "notANumber";
  const isMonthlyPaymentNegative = parsedMonthlyPayment.kind === "number" && parsedMonthlyPayment.value < 0;
  const isInterestRateNotANumber = parseInterestRateInput(interestRate ?? "").kind === "notANumber";
  const hasRefusedInput =
    isInterestRateNotANumber ||
    isMonthlyPaymentNotANumber ||
    isMonthlyPaymentNegative ||
    Object.keys(rejectedMonthInput).length > 0;
  const isReportReady = hasValuesForIfrsReport(ifrsValues) && !hasRefusedInput;

  return (
    <>
      {showAddAdjustment && (
        <IfrsAdjustmentsModal
          contract={contract as ContractDto}
          onClose={() => setShowAddAdjustment(false)}
        />
      )}
      <Flex justifyContent={"space-between"}>
        <Heading fontSize="lg">
          {t("IFRS reporting")} {entityStatus === "saving" && <Spinner size="xs" />}
        </Heading>
        <RemoveDataFieldButton removeDataField={removeDataField} />
      </Flex>
      <DateFormControl
        id="ifrs start date"
        title={t("Start date")}
        isRequiredButNotProvided={isRequiredButNotProvided}
        helperText=""
        errorMessage={null}
        defaultDate={ifrsValues.startDate ?? undefined}
        onChange={async (startDate) => {
          handleIfrsValueChange({ startDate: startDate ? startDate : null });
        }}
      />
      {saveError("startDate")}
      <FormControl width="full" py="5">
        <FormLabel fontSize="sm">{t("Second period start date")}</FormLabel>
        <Flex width="full">
          <Flex flexDirection="column" justify="space-between" grow="1" width="50%" pt="6" pl="4">
            <Box>
              <Checkbox
                size="lg"
                colorScheme="teal"
                backgroundColor="smBackground"
                isChecked={hasDeviatingFirstPeriod}
                onChange={(e) => {
                  const isChecked = e.target.checked;
                  setHasDeviatingFirstPeriod(isChecked);
                  if (!isChecked) handleIfrsValueChange({ startDateSecondPeriod: null });
                }}
                aria-label="deviating first period"
              />
            </Box>
            <FormHelperText>{t("Has deviating first period")}</FormHelperText>
          </Flex>
          <Box width="50%">
            <DateSelector
              isDisabled={!hasDeviatingFirstPeriod}
              defaultDate={ifrsValues?.startDateSecondPeriod ?? undefined}
              onChange={async (startDateSecondPeriod) => {
                handleIfrsValueChange({ startDateSecondPeriod: startDateSecondPeriod ? startDateSecondPeriod : null });
              }}
            />
            <FormHelperText>{t("Select second period start date in case of deviating first period")}</FormHelperText>
            {saveError("startDateSecondPeriod")}
          </Box>
        </Flex>
      </FormControl>
      <FormControl pb="5" isInvalid={isMonthlyPaymentNotANumber || isMonthlyPaymentNegative}>
        <FormLabel fontSize="sm" pb="1" htmlFor="ifrs-monthly-payment">
          {t("Monthly payment")}
        </FormLabel>
        <InputGroup>
          <InputLeftAddon>{currency}</InputLeftAddon>
          <Input
            id="ifrs-monthly-payment"
            inputMode="decimal"
            autoComplete="off"
            placeholder={t("Monthly payment") ?? ""}
            backgroundColor="smBackground"
            value={monthlyPaymentText}
            onFocus={() => {
              monthlyPaymentBeforeEdit.current = ifrsValues.monthlyPayment ?? null;
            }}
            onChange={(e) => {
              setMonthlyPaymentText(e.target.value);
              const parsed = parseDecimalInput(e.target.value, { allowSpaceGrouping: true });
              if (parsed.kind === "notANumber" || (parsed.kind === "number" && parsed.value < 0)) {
                const beforeEdit = monthlyPaymentBeforeEdit.current;
                if (beforeEdit !== undefined && (ifrsValues.monthlyPayment ?? null) !== beforeEdit) {
                  handleIfrsValueChange({ monthlyPayment: beforeEdit });
                }
                return;
              }
              handleIfrsValueChange({ monthlyPayment: parsed.kind === "number" ? parsed.value : null });
            }}
          />
        </InputGroup>
        <FormErrorMessage>
          {isMonthlyPaymentNegative
            ? t("The amount cannot be negative. The value is not saved.")
            : t("Write the amount as a number, for example 1 234,50")}
        </FormErrorMessage>
        {saveError("monthlyPayment")}
      </FormControl>
      <FormControl pb="5" isInvalid={isMonthFieldInvalid("paymentIntervalInMonths")}>
        <FormLabel fontSize="sm" pb="1">
          {t("Payment interval")}
        </FormLabel>
        <Input
          mt="5px"
          type="number"
          placeholder={`${t("Provide payment interval in number of months")}...`}
          backgroundColor="smBackground"
          value={monthInputValue("paymentIntervalInMonths")}
          min={1}
          onChange={(e) => handleMonthsChange("paymentIntervalInMonths", e.target)}
        />
        <FormErrorMessage>{belowOneMonthMessage("paymentIntervalInMonths")}</FormErrorMessage>
        {saveError("paymentIntervalInMonths")}
      </FormControl>
      <FormControl pb="5" isInvalid={isMonthFieldInvalid("contractLengthInMonths")}>
        <FormLabel fontSize="sm" pb="1">
          {t("Contract length")}
        </FormLabel>
        <Input
          type="number"
          placeholder={`${t("Provide contract length in number of months")}...`}
          backgroundColor="smBackground"
          value={monthInputValue("contractLengthInMonths")}
          min={1}
          onChange={(e) => handleMonthsChange("contractLengthInMonths", e.target)}
        />
        <FormErrorMessage>{belowOneMonthMessage("contractLengthInMonths")}</FormErrorMessage>
        {saveError("contractLengthInMonths")}
      </FormControl>
      <FormControl pb="5" isInvalid={isInterestRateNotANumber}>
        <FormLabel fontSize="sm" pb="1">
          {t("Interest rate (%)")}
        </FormLabel>
        <Input
          inputMode="decimal"
          autoComplete="off"
          placeholder={`${t("Interest rate (%)")}...`}
          backgroundColor="smBackground"
          value={interestRate ?? ""}
          onChange={(e) => {
            setInterestRate(e.target.value);
            const parsed = parseInterestRateInput(e.target.value);
            if (parsed.kind === "notANumber") return;
            handleIfrsValueChange({ interestRate: parsed.kind === "rate" ? parsed.fraction : null });
          }}
        />
        <FormErrorMessage>{t("Write the interest rate as a number, for example 3,7")}</FormErrorMessage>
        {saveError("interestRate")}
      </FormControl>
      <Text>{t("The values are used for IFRS reporting")}</Text>
      <Explanation
        fontSize="sm"
        enabled={!savedValuesFilled}
        text={t("Please fill out all other fields before adding adjustments")}
      >
        <Button
          mt="10px"
          size="sm"
          leftIcon={<Icon as={FaChartLine} w="15px" h="15px" />}
          variant={"solid"}
          colorScheme="green"
          onClick={() => setShowAddAdjustment(true)}
          isDisabled={!savedValuesFilled}
        >
          {t("Adjustments")}{" "}
          {adjustments.length > 0 && `(${adjustments.length})`}
        </Button>
      </Explanation>
      <Box mt="7px">
        <Explanation
          fontSize="sm"
          text={
            hasRefusedInput
              ? t("Correct the values that are not saved before downloading the report")
              : t("Please fill out all values before downloading the report")
          }
          enabled={!isReportReady}
        >
          <Button
            size="sm"
            leftIcon={<Icon as={FaDownload} w="15px" h="15px" />}
            isDisabled={!isReportReady}
            isLoading={isDownloadingReport}
            variant={"solid"}
            colorScheme="blue"
            onClick={downloadReport}
          >
            {t("Download IFRS report")}
          </Button>
        </Explanation>
      </Box>
    </>
  );
};

type MonthField = "paymentIntervalInMonths" | "contractLengthInMonths";

const isBelowOneMonth = (months: number | null | undefined): boolean => !isNil(months) && !(months >= 1);
